import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

interface InputMessage {
	text: string;
	status: "loading" | "success" | "error" | "message";
}

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
	sizeVariant?: "default" | "large";
	label?: ReactNode;
	error?: string;
	message?: InputMessage;
	required?: boolean;
	inputPrefix?: ReactNode;
	inputSuffix?: ReactNode;
	variant?: "light" | "dark";
}

const sizeVariantLabelStyles: Record<NonNullable<InputProps["sizeVariant"]>, string> = {
	default: "mb-2 text-sm",
	large: "text-base",
};

const sizeVariantInputStyles: Record<NonNullable<InputProps["sizeVariant"]>, string> = {
	default: "py-2 px-4",
	large: "py-3 px-5",
};

const Input = forwardRef<HTMLInputElement, InputProps>(({ sizeVariant = "default", label, error, message, id, className = "", required, inputPrefix, inputSuffix, variant = "light", ...props }, ref) => {
	const inputId = id || props.name;
	const isDark = variant === "dark";

	const renderMessage = () => {
		if (error) {
			return (
				<div className="mt-1.5 flex items-center gap-1.5 text-xs">
					<span className="text-red-500">{error}</span>
				</div>
			);
		}

		if (message) {
			if (message.status === "loading") {
				return (
					<div className="mt-1.5 flex items-center gap-1.5 text-xs">
						<span className="text-tertiary">{message.text}</span>
					</div>
				);
			}

			if (message.status === "error") {
				return (
					<div className="mt-1.5 flex items-center gap-1.5 text-xs">
						<svg className="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
						<span className="text-red-500">{message.text}</span>
					</div>
				);
			}

			if (message.status === "success") {
				return (
					<div className="mt-1.5 flex items-center gap-1.5 text-xs">
						<svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
						</svg>
						<span className="text-green-500">{message.text}</span>
					</div>
				);
			}

			if (message.status === "message") {
				return (
					<div className="mt-1.5 flex items-center gap-1.5 text-xs">
						<span className="text-tertiary">{message.text}</span>
					</div>
				);
			}
		}

		return null;
	};

	const inputElement = (
		<input
			ref={ref}
			id={inputId}
			className={`w-full border font-sans text-sm focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
				isDark ? "bg-gray-800 border-gray-700 text-white placeholder:text-gray-500" : "bg-white border-gray-300 text-primary placeholder:text-gray-400"
			} placeholder:font-light ${
				error || message?.status === "error" ? "border-red-500" : ""
			} ${inputPrefix ? "rounded-r-lg border-l-0 focus:ring-0" : "rounded-lg focus:ring-2 focus:ring-brand focus:ring-offset-1"} ${sizeVariantInputStyles[sizeVariant]} ${className}`}
			{...props}
		/>
	);

	return (
		<div>
			{label && (
				<label htmlFor={inputId} className={`block font-medium text-primary font-sans ${sizeVariantLabelStyles[sizeVariant]}`}>
					{label}
					{required && <span className="text-red-500"> *</span>}
				</label>
			)}
			{inputPrefix ? (
				<div className={`flex rounded-lg focus-within:ring-2 focus-within:ring-brand focus-within:ring-offset-1 ${
					error || message?.status === "error" ? "focus-within:ring-red-500" : ""
				}`}>
					<span className={`inline-flex items-center px-3 border border-r-0 rounded-l-lg ${
						isDark ? "bg-gray-700 border-gray-700" : "bg-gray-50 border-gray-300"
					} ${error || message?.status === "error" ? "border-red-500" : ""}`}>
						{inputPrefix}
					</span>
					{inputElement}
				</div>
			) : inputSuffix ? (
				<div className="relative">
					{inputElement}
					<div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
						{inputSuffix}
					</div>
				</div>
			) : (
				inputElement
			)}
			{renderMessage()}
		</div>
	);
});

Input.displayName = "Input";

export default Input;
